import {
  type GetVisitRequestQuery,
  type GetVisitWithCaseDataResponse,
  getVisitWithCaseDataResponse,
} from "@clipboard-health/contract-worker-app-bff";
import { isDefined } from "@clipboard-health/util-ts";
import { useGetQuery, type UseGetQueryOptions } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { type GeoLocation } from "@src/appV2/Location";

interface UseGetVisitParams {
  visitId: string | undefined;
  caseId: string | undefined;
  geoLocation: GeoLocation | undefined;
  includeOccurrences?: boolean;
}

export const GET_VISIT_URL = `${environmentConfig.REACT_APP_WORKER_APP_BFF_URL}/in-home/case/:caseId/visit/:visitId`;

export function getVisitQueryKey(visitId: string, caseId: string) {
  return [GET_VISIT_URL.replace(":visitId", visitId).replace(":caseId", caseId)];
}

export function useGetVisit(
  params: UseGetVisitParams,
  options: UseGetQueryOptions<GetVisitWithCaseDataResponse> = {}
) {
  const { visitId, caseId, geoLocation, includeOccurrences } = params;

  const includeStatement: GetVisitRequestQuery["include"] = ["case"];

  if (includeOccurrences) {
    includeStatement.push("visitOccurrence");
  }

  const queryParams: GetVisitRequestQuery = {
    filter: {
      latitude: geoLocation?.latitude ?? 0,
      longitude: geoLocation?.longitude ?? 0,
    },
    include: includeStatement,
  };

  return useGetQuery({
    url: GET_VISIT_URL.replace(":visitId", visitId ?? "").replace(":caseId", caseId ?? ""),
    queryParams,
    responseSchema: getVisitWithCaseDataResponse,
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.GET_VISIT_DATA_FAILURE,
    },
    retry: 0,
    staleTime: 1000 * 60 * 1,
    ...options,
    enabled: isDefined(visitId) && isDefined(caseId) && isDefined(geoLocation),
  });
}
